import React, { useState } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Link,
  Chip,
  ListItemIcon,
  Divider,
  Button,
  CircularProgress,
} from '@mui/material';
import { Logo } from '@horwath-htl/shared-branding';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  Logout,
  ShoppingCart as ShoppingCartIcon,
  CardMembership as SubscriptionsIcon,
  DeveloperMode as DeveloperIcon,
  Login as LoginIcon,
  Speed as SpeedIcon,
  Mail as MailIcon,
} from '@mui/icons-material';
import { Outlet, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useQueryClient } from '@tanstack/react-query';
import { useCurrentUser, type UserInfo } from '../hooks/useApi';
import { useCart } from '../hooks/useCart';
import { useAppNavigation } from '../hooks/useAppNavigation';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { RouteKeyWrapper } from './routing/RouteKeyWrapper';
import { CartDrawer } from './cart/CartDrawer';
import { CookieConsent } from './CookieConsent';
import { API_URL } from '../utils/config';
import { useOTPAuth } from '../auth/providers/OTPAuthProvider';
import { brandColors } from '../theme';
import { useMessageNotifications } from '../hooks/useMessagePolling';

export const Layout: React.FC = () => {
  const { navigateTo } = useAppNavigation();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { instance, accounts } = useMsal();
  const { isAuthenticated: isOTPAuthenticated, user: otpUser, logout: otpLogout } = useOTPAuth();
  const { data: currentUser } = useCurrentUser() as { data: UserInfo | null | undefined };
  const { itemCount, clearCart } = useCart();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const isMsalAuthenticated = accounts.length > 0;
  const isAuthenticated = isMsalAuthenticated || isOTPAuthenticated;
  const { unreadCount } = useMessageNotifications();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [rolePermissions, setRolePermissions] = useState<Array<{ id: string; name: string; description: string }>>([]);
  const [loadingPermissions, setLoadingPermissions] = useState(false);

  // Only refetch user data when authentication state changes from false to true (initial login)
  const [prevAuthenticated, setPrevAuthenticated] = useState(isAuthenticated);
  React.useEffect(() => {
    // Only invalidate if transitioning from not authenticated to authenticated (login)
    if (isAuthenticated && !prevAuthenticated) {
      // User just logged in, invalidate to get fresh data
      queryClient.invalidateQueries({ queryKey: ['currentUser'], exact: false });
    }
    setPrevAuthenticated(isAuthenticated);
  }, [isAuthenticated, prevAuthenticated, queryClient]);

  // Handle the clear cart event for interval mismatch
  React.useEffect(() => {
    const handleClearCartForInterval = () => {
      clearCart();
    };

    window.addEventListener('clearCartForInterval', handleClearCartForInterval);
    return () => {
      window.removeEventListener('clearCartForInterval', handleClearCartForInterval);
    };
  }, [clearCart]);

  // Create main navigation items
  const navigationItems: Array<
    | { text: string; path: string; external?: false }
    | { text: string; url: string; external: true }
  > = [
    { text: 'MarketCompass Reports', path: '/global-market-reports' },
    { text: 'Custom Comp Sets', path: '/custom-comp-sets' },
    { text: 'Insights', url: 'https://horwathhtl.com/insights/', external: true },
    ...(isAuthenticated
      ? [{ text: 'Your Purchases', path: '/subscriptions' }]
      : []),
  ];

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
    setSelectedRole(null);
    setRolePermissions([]);
  };

  const handleRoleClick = async (event: React.MouseEvent<HTMLElement>, roleName: string) => {
    event.stopPropagation();
    
    // Toggle permissions display if clicking the same role
    if (selectedRole === roleName) {
      setSelectedRole(null);
      setRolePermissions([]);
      return;
    }
    
    setSelectedRole(roleName);
    setLoadingPermissions(true);
    
    try {
      const response = await authenticatedFetch(`${API_URL}/api/auth/role-permissions?roleName=${encodeURIComponent(roleName)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response && response.ok) {
        const data = await response.json();
        if (data.success && data.data && data.data.permissions) {
          setRolePermissions(data.data.permissions);
        } else {
          setRolePermissions([{ id: '0', name: 'error', description: 'Failed to load permissions' }]);
        }
      } else {
        setRolePermissions([{ id: '0', name: 'error', description: 'Failed to fetch permissions' }]);
      }
    } catch (error) {
      console.error('Failed to fetch role permissions:', error);
      setRolePermissions([{ id: '0', name: 'error', description: 'Error loading permissions' }]);
    } finally {
      setLoadingPermissions(false);
    }
  };

  const handleLogout = async () => {
    // Clear ALL React Query cache data
    queryClient.clear();
    
    // Clear all localStorage data for the site
    localStorage.clear();
    
    // Clear all sessionStorage data for the site
    sessionStorage.clear();
    
    // Clear the cart
    clearCart();
    
    if (isOTPAuthenticated) {
      await otpLogout();
      navigateTo('/');
    } else {
      instance.logoutRedirect({
        postLogoutRedirectUri: '/',
      });
    }
  };

  const handleNavigateToAdmin = (path: string) => {
    navigateTo(path);
    handleProfileMenuClose();
  };

  const handleManageSubscriptions = async () => {
    try {
      const response = await authenticatedFetch(`${API_URL}/api/store/customerPortal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response && response.ok) {
        const data = await response.json();
        if (data.portalUrl) {
          window.location.href = data.portalUrl;
        }
      }
    } catch (error) {
      console.error('Failed to get customer portal URL:', error);
    }
    handleProfileMenuClose();
  };

  const currentAccount = accounts[0];
  const displayName = otpUser?.name || currentUser?.email?.split('@')[0] || currentAccount?.name || 'User';
  const displayEmail = otpUser?.email || currentUser?.email || currentAccount?.username || '';
  const isFullBleedPage =
    location.pathname === '/' ||
    location.pathname === '/dashboard' ||
    location.pathname === '/global-market-reports' ||
    location.pathname === '/custom-comp-sets';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', height: '100%' }}>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: 'rgba(255,255,255,0.85)',
          backdropFilter: 'saturate(180%) blur(10px)',
          color: brandColors.indigo,
          boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
          borderBottom: '1px solid rgba(17,35,55,0.08)',
        }}
      >
        <Toolbar sx={{ gap: 2, minHeight: 72, position: 'relative' }}>
          {/* Mobile Navigation Button - Left side on mobile */}
          <IconButton
            color="inherit"
            aria-label="open menu"
            edge="start"
            onClick={handleMobileMenuOpen}
            sx={{ display: { xs: 'flex', md: 'none' }, position: 'absolute', left: 8 }}
          >
            <MenuIcon />
          </IconButton>

          {/* Logo - clickable to go home, centered on mobile */}
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mr: { xs: 0, md: 3 },
              cursor: 'pointer',
              '& *': { cursor: 'pointer' },
              // Center on mobile
              position: { xs: 'absolute', md: 'relative' },
              left: { xs: '50%', md: 'auto' },
              transform: { xs: 'translateX(-50%)', md: 'none' },
            }}
            onClick={() => navigateTo('/')}
          >
            <Logo height={35} />
          </Box>

          {/* Desktop Navigation */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2, flexGrow: 1 }}>
            {navigationItems.map((item) => {
              const isActive = 'path' in item && (location.pathname === item.path || (item.path === '/dashboard' && location.pathname === '/'));
              return (
                <Button
                  key={item.text}
                  onClick={() => {
                    if ('external' in item && item.external) {
                      window.open(item.url, '_blank', 'noopener');
                    } else if ('path' in item) {
                      navigateTo(item.path);
                    }
                  }}
                  sx={{
                    textTransform: 'none',
                    color: brandColors.indigo,
                    backgroundColor: 'transparent',
                    fontWeight: 500,
                    letterSpacing: '0.3px',
                    fontSize: '0.95rem',
                    py: 1,
                    borderRadius: 0,
                    borderBottom: isActive ? `3px solid ${brandColors.gold}` : '3px solid transparent',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: brandColors.gold,
                      borderBottomColor: brandColors.gold,
                    },
                  }}
                >
                  {item.text}
                </Button>
              );
            })}
          </Box>

          {/* Spacer for mobile to push icons to the right */}
          <Box sx={{ flexGrow: 1 }} />

          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            position: { xs: 'absolute', md: 'relative' },
            right: { xs: 8, md: 'auto' }
          }}>
            {/* Notification icon for messages - hidden on mobile */}
            {isAuthenticated && (
              <IconButton 
                color="inherit" 
                onClick={() => navigateTo('/messages')}
                sx={{ 
                  color: brandColors.indigo,
                  display: { xs: 'none', sm: 'inline-flex' } // Hide on mobile
                }}
              >
                <Badge badgeContent={unreadCount} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            )}

            {/* Shopping cart icon - hidden on mobile */}
            <IconButton 
              color="inherit" 
              onClick={() => setCartOpen(true)} 
              sx={{ 
                color: brandColors.indigo,
                display: { xs: 'none', sm: 'inline-flex' } // Hide on mobile
              }}
            >
              <Badge badgeContent={itemCount} color="error">
                <ShoppingCartIcon />
              </Badge>
            </IconButton>

            {isAuthenticated ? (
              <IconButton
                size="large"
                edge="end"
                aria-label="account of current user"
                aria-controls="profile-menu"
                aria-haspopup="true"
                onClick={handleProfileMenuOpen}
                color="inherit"
              >
                <Avatar sx={{ width: 32, height: 32 }}>
                  {displayName.charAt(0).toUpperCase()}
                </Avatar>
              </IconButton>
            ) : (
              <Button
                variant="text"
                startIcon={<LoginIcon />}
                onClick={() => navigateTo('/login', { state: { from: location.pathname + location.search } })}
                sx={{
                  color: brandColors.indigo,
                  '&:hover': {
                    backgroundColor: 'transparent',
                    color: brandColors.gold,
                  },
                  display: { xs: 'none', md: 'flex' }, // Hide on mobile, show on desktop
                }}
              >
                Login
              </Button>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Menu */}
      <Menu
        anchorEl={mobileMenuAnchor}
        open={Boolean(mobileMenuAnchor)}
        onClose={handleMobileMenuClose}
        PaperProps={{
          sx: { width: '100%', maxWidth: 360 }
        }}
      >
        {navigationItems.map((item) => {
          const isInternal = 'path' in item;
          const isSelected = isInternal && (location.pathname === item.path || (item.path === '/dashboard' && location.pathname === '/'));
          return (
            <MenuItem
              key={item.text}
              onClick={() => {
                if (isInternal) {
                  navigateTo(item.path);
                } else {
                  window.open(item.url, '_blank', 'noopener');
                }
                handleMobileMenuClose();
              }}
              selected={Boolean(isSelected)}
            >
              {item.text}
            </MenuItem>
          );
        })}
        
        {/* Shopping cart and notifications for mobile */}
        <Divider />
        
        {/* Messages/Notifications - only for authenticated users */}
        {isAuthenticated && (
          <MenuItem
            onClick={() => {
              navigateTo('/messages');
              handleMobileMenuClose();
            }}
          >
            <ListItemIcon>
              <NotificationsIcon />
            </ListItemIcon>
            Notifications
            {unreadCount > 0 && (
              <Chip 
                label={unreadCount} 
                size="small" 
                color="error" 
                sx={{ ml: 'auto', height: 20, minWidth: 20 }} 
              />
            )}
          </MenuItem>
        )}
        
        {/* Shopping Cart - always available */}
        <MenuItem
          onClick={() => {
            setCartOpen(true);
            handleMobileMenuClose();
          }}
        >
          <ListItemIcon>
            <ShoppingCartIcon />
          </ListItemIcon>
          Shopping Cart
          {itemCount > 0 && (
            <Chip 
              label={itemCount} 
              size="small" 
              color="primary" 
              sx={{ ml: 'auto', height: 20, minWidth: 20 }} 
            />
          )}
        </MenuItem>
        
        {/* Login button for mobile - only show when not authenticated */}
        {!isAuthenticated && (
          <>
            <Divider />
            <MenuItem
              onClick={() => {
                navigateTo('/login', { state: { from: location.pathname + location.search } });
                handleMobileMenuClose();
              }}
            >
              <ListItemIcon>
                <LoginIcon />
              </ListItemIcon>
              Login
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Profile Menu - only render when authenticated */}
      {isAuthenticated && (
        <Menu
          id="profile-menu"
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleProfileMenuClose}
          PaperProps={{
            sx: { minWidth: 250 }
          }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              {displayName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {displayEmail}
            </Typography>
            {currentUser?.roles && currentUser.roles.length > 0 && (
              <Box sx={{ 
                display: { xs: 'none', sm: 'flex' }, // Hide on mobile
                flexWrap: 'wrap', 
                gap: 0.5, 
                mt: 1 
              }}>
                {currentUser.roles.map((role) => (
                  <Chip 
                    key={role.id}
                    label={role.name} 
                    size="small" 
                    color={selectedRole === role.name ? "primary" : "default"}
                    variant={selectedRole === role.name ? "filled" : "outlined"}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleRoleClick(e, role.name);
                    }}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      }
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
          <Divider />
          
          {/* Mobile-only menu items for notifications and cart */}
          <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
            <MenuItem onClick={() => handleNavigateToAdmin('/messages')}>
              <ListItemIcon>
                <NotificationsIcon fontSize="small" />
              </ListItemIcon>
              Notifications
              {unreadCount > 0 && (
                <Chip 
                  label={unreadCount} 
                  size="small" 
                  color="error" 
                  sx={{ ml: 'auto', height: 20, minWidth: 20 }} 
                />
              )}
            </MenuItem>
            <MenuItem onClick={() => { setCartOpen(true); handleProfileMenuClose(); }}>
              <ListItemIcon>
                <ShoppingCartIcon fontSize="small" />
              </ListItemIcon>
              Shopping Cart
              {itemCount > 0 && (
                <Chip 
                  label={itemCount} 
                  size="small" 
                  color="primary" 
                  sx={{ ml: 'auto', height: 20, minWidth: 20 }} 
                />
              )}
            </MenuItem>
            <Divider />
          </Box>
          
          {/* Desktop and mobile messages menu item */}
          <MenuItem onClick={() => handleNavigateToAdmin('/messages')} sx={{ display: { xs: 'none', sm: 'flex' } }}>
            <ListItemIcon>
              <MailIcon fontSize="small" />
            </ListItemIcon>
            Messages
            {unreadCount > 0 && (
              <Chip 
                label={unreadCount} 
                size="small" 
                color="error" 
                sx={{ ml: 'auto', height: 20, minWidth: 20 }} 
              />
            )}
          </MenuItem>
          <MenuItem onClick={handleManageSubscriptions}>
            <ListItemIcon>
              <SubscriptionsIcon fontSize="small" />
            </ListItemIcon>
            Manage Subscriptions
          </MenuItem>
          
          {/* Admin Menu Items */}
          {currentUser?.permissions?.includes('developer_tools.view') && (
            <>
              <Divider />
              <MenuItem onClick={() => handleNavigateToAdmin('/admin/api-debugging')}>
                <ListItemIcon>
                  <DeveloperIcon fontSize="small" />
                </ListItemIcon>
                Developer Tools
              </MenuItem>
              <MenuItem onClick={() => handleNavigateToAdmin('/performance')}>
                <ListItemIcon>
                  <SpeedIcon fontSize="small" />
                </ListItemIcon>
                Performance Monitoring
              </MenuItem>
            </>
          )}
          
          <Divider />
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <Logout fontSize="small" />
            </ListItemIcon>
            Logout
          </MenuItem>
          
          {/* Role Permissions Display - only on desktop */}
          {selectedRole && (
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Divider />
              <Box sx={{ px: 2, py: 1.5, backgroundColor: 'action.hover' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  {selectedRole} Permissions:
                </Typography>
                {loadingPermissions ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                    <CircularProgress size={20} />
                  </Box>
                ) : rolePermissions.length > 0 ? (
                  <Box sx={{ pl: 1 }}>
                    {rolePermissions.map((permission) => (
                      <Typography key={permission.id} variant="body2" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
                        • {permission.description}
                      </Typography>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                    No permissions available
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Menu>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: isFullBleedPage ? 0 : 3,
          mt: 8, // Space for the fixed AppBar
          width: '100%',
        }}
      >
        <RouteKeyWrapper>
          <Outlet />
        </RouteKeyWrapper>
      </Box>
      
      <Box
        component="footer"
        sx={{
          backgroundColor: '#011E41', // brandColors.indigo
          color: 'white',
          py: 2,
          px: 3,
          mt: 'auto',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography variant="body2">
            © Copyright Barrett HTL Inc 2025 - HTL MarketCompass Store
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Link
              href="/privacy"
              onClick={(e) => {
                e.preventDefault();
                navigateTo('/privacy');
              }}
              color="inherit"
              underline="hover"
              sx={{
                fontSize: '0.875rem',
                cursor: 'pointer',
                '&:hover': {
                  color: '#F5A800', // brandColors.gold
                },
              }}
            >
              Privacy Policy
            </Link>
            
            <Link
              href="/eula"
              onClick={(e) => {
                e.preventDefault();
                navigateTo('/eula');
              }}
              color="inherit"
              underline="hover"
              sx={{
                fontSize: '0.875rem',
                cursor: 'pointer',
                '&:hover': {
                  color: '#F5A800', // brandColors.gold
                },
              }}
            >
              Terms of Use
            </Link>
          </Box>
        </Box>
      </Box>
      
      {/* Cart Drawer */}
      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
      
      {/* Cookie Consent Banner */}
      <CookieConsent />
    </Box>
  );
};